import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { Typography, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios, { AxiosError } from 'axios';
import {
  uploadAttachment,
  setAddCaseResponseInfo,
  resetAddCase,
  setCaseMemberInfo,
  setProviderDescriptionInfo,
} from '@apps/shared/src/addCase/actions';

import { MemberInfo } from '@apps/shared/src/getMember/types/memberTypes';
import { getAxiosRequestConfig } from '@apps/shared/src/utilities/http';
import { AddCaseState, ProviderDescription } from '@apps/shared/src/addCase/types/addCaseTypes';
import a from '@apps/shared/src/addCase/types/addCaseActions';

import { RootState } from '../store';
import TextInput from '../shared/textInput';
import DragDrop from './dragDrop';
import LoaderDialog from '../shared/loaderDialog';
import SuccessDialog from '../shared/successDialog';
import AppLayout from '../shared/AppLayout';

const useStyles = makeStyles({
  appContainer: {
    color: '#797C80',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    position: 'relative',
    height: 'calc(100vh - 40px - 2.5rem)',
  },
  arrowIcon: {
    height: '2rem',
    width: '2rem',
    cursor: 'pointer',
  },
  formWrapper: {
    display: 'flex',
    alignItems: 'start',
    gap: '1rem',
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      width: '1px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '1px',
    },
  },
  textBox: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    width: '50%',
    '&.MuiOutlinedInput-root': {
      borderRadius: '.5rem',
    },
    '& .MuiInputBase-root': {
      height: '30vh',
    },
    '& .MuiInputBase-inputMultiline': {
      height: 'inherit !important',
    },
  },
  buttonStyle: {
    width: '32%',
    position: 'absolute',
    bottom: 0,
    right: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1.5rem',
  },
  backBtn: {
    gridColumn: '1 / -1',
    width: '50%',
    borderRadius: '.5rem',
    background: '#fff',
    color: '#888787',
    border: '1px solid #888787',
    padding: '.6rem 1rem',
    cursor: 'pointer',
    boxShadow: 'None',
  },
  successBtn: {
    gridColumn: '1 / -1',
    width: '50%',
    borderRadius: '.5rem',
    background: 'primary',
    color: 'white',
    fontWeight: 600,
    outline: 'None',
    padding: '.6rem',
    cursor: 'pointer',
    boxShadow: 'None',
  },
  loadingSpinner: {
    margin: '3rem auto 0 auto',
  },
  link: {
    color: '#366BED',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0)', // Initial transparent background
    zIndex: -1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0, // Initial opacity set to 0
    transition: 'background 0.3s ease, opacity 0.3s ease', // Transition properties
  },
  overlayVisible: {
    background: 'rgba(255, 255, 255, 0.8)', // Visible background color
    opacity: 1, // Visible opacity
    zIndex: 999,
  },
  heading: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#504f4f',
    marginBottom: '1rem',
  },
  textBoxWrapper: {
    display: 'grid',
    gap: '1.5rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(30%, 1fr))',
  },
});

type StateProps = {
  newCaseInfo: any;
  providerInfoDescription: any;
  caseId: string;
  isCaseCreated: boolean;
  caseType: string;
  addingCase: boolean;
  hasClientSubmitRole: boolean;
};

type DispatchProps = {
  setAddCaseResponseInfo: (
    key: keyof AddCaseState,
    value: AddCaseState[keyof AddCaseState]
  ) => void;
  uploadAttachment: (redirectTo: string, caseID: string) => void;
  resetAddCase: () => void;
  setCaseMemberInfo: (key: keyof MemberInfo, value: MemberInfo[keyof MemberInfo]) => void;
  setProviderDescriptionInfo: (key: keyof ProviderDescription, value: string) => void;
};

const mapDispatchToProps = {
  setAddCaseResponseInfo,
  uploadAttachment,
  resetAddCase,
  setCaseMemberInfo,
  setProviderDescriptionInfo,
};

const mapStateToProps = (state: RootState) => ({
  newCaseInfo: state.addCase.memberInfo,
  providerInfoDescription: state.addCase.providerInfo.description,
  caseId: state.addCase.caseId,
  isCaseCreated: state.addCase.isCreated,
  caseType: state.addCase.caseType,
  addingCase: state.addCase.addingCase,
  hasClientSubmitRole:
    state.user.currentUser?.roles?.includes('medivi-casesubmissionclient') ?? false,
});

type Props = StateProps & DispatchProps;

const UploadFiles = ({
  newCaseInfo,
  providerInfoDescription,
  caseType,
  isCaseCreated,
  setAddCaseResponseInfo,
  uploadAttachment,
  addingCase,
  hasClientSubmitRole,
  resetAddCase,
  setCaseMemberInfo,
  setProviderDescriptionInfo,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [comments, setComments] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const handleBack = () => {
    history.goBack();
  };

  useEffect(() => {
    if (caseType === '') {
      history.push(`/my-cases`);
    }
    // eslint-disable-next-line
  }, []);

  const handleFilesAccepted = useCallback((acceptedFiles: File[]) => {
    setUploadedFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, []);

  const handlePSSEmailClick = () => {
    let email = 'PSS@6degreeshealth.com';

    if (newCaseInfo.groupid === '10000SDH' || newCaseInfo.groupid === '0180988601') {
      email = 'employeehealthplan@6degreeshealth.com';
    }

    const mailtoLink = `mailto:${email}`;

    window.location.href = mailtoLink;
  };

  const handleCSSEmailClick = () => {
    const email = 'CSS@6degreeshealth.com';

    const mailtoLink = `mailto:${email}`;

    window.location.href = mailtoLink;
  };

  const handleSubmit = useCallback(async () => {
    const caseDetail = { caseId: '', isCreated: '' };
    dispatch({ type: a.ADD_CASE_PENDING });

    try {
      setLoading(true);

      const isClientSubmit = hasClientSubmitRole;

      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const createCaseUrl = isClientSubmit
        ? `${baseUrl}/api/createClientCase`
        : `${baseUrl}/api/createCase`;

      const uploadUrl = isClientSubmit
        ? `${baseUrl}/api/case/uploadClientAttachment`
        : `${baseUrl}/api/case/uploadAttachment`;

      // Determine recordType based on caseType
      let recordType: string;
      switch (caseType) {
        case 'Balance Bill':
          recordType = 'BalanceBill';
          break;
        case 'Call Ahead':
          recordType = 'CallAhead';
          break;
        case 'Appeal':
          recordType = 'Appeal';
          break;
        default:
          recordType = 'AccessIssue';
      }

      const payload: any = {
        ...newCaseInfo,
        recordType,
      };

      if (isClientSubmit) {
        payload.description = {
          planname: providerInfoDescription.planname,
          groupid: providerInfoDescription.groupid,
          tpaName: providerInfoDescription.tpaName,
        };
      }

      const result = await axios.post(createCaseUrl, payload, getAxiosRequestConfig());

      const newCaseId = result.data.id;
      caseDetail.caseId = newCaseId;

      if (uploadedFiles.length > 0) {
        try {
          const formData = new FormData();
          formData.append('caseID', newCaseId);
          formData.append('contentLocation', '');
          formData.append('title', newCaseId);

          uploadedFiles.forEach(file => {
            formData.append('files', file, file.name);
          });

          const uploadResult = await axios.post(uploadUrl, formData, getAxiosRequestConfig());

          caseDetail.isCreated = uploadResult.data.success;
        } catch (error) {
          if ((error as AxiosError).response) {
            const axiosError = error as AxiosError;
            const errorResponse = axiosError.response?.data;
            // addMessage(errorResponse);
          }
        }
      }

      setAddCaseResponseInfo('isCreated', caseDetail.isCreated);
      setAddCaseResponseInfo('caseId', caseDetail.caseId);

      dispatch({
        type: a.ADD_CASE_FULFILLED,
        payload: { ...result, caseId: newCaseId },
      });

      setLoading(false);
    } catch (error) {
      if ((error as AxiosError).response) {
        const axiosError = error as AxiosError;
        const errorResponse = axiosError.response?.data;
        // addMessage(errorResponse);
      }
      setLoading(false);
    }
  }, [
    dispatch,
    setAddCaseResponseInfo,
    setLoading,
    newCaseInfo,
    caseType,
    uploadedFiles,
    hasClientSubmitRole,
    providerInfoDescription,
  ]);

  const handleChange = (value: string, key: keyof MemberInfo) => {
    setComments(value);
    setCaseMemberInfo(key, value);
  };

  const handlePlanInfoChange =
    (field: keyof ProviderDescription) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setProviderDescriptionInfo(field, e.target.value);
    };

  const handleSuccess = (): void => {
    resetAddCase();
    history.push('/my-cases');
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };

  const disableSubmitBtn =
    uploadedFiles.length === 0 ||
    (hasClientSubmitRole &&
      ((newCaseInfo.planname || providerInfoDescription.planname || '').trim() === '' ||
        (newCaseInfo.groupid || providerInfoDescription.groupid || '').trim() === '' ||
        (newCaseInfo.tpaName || providerInfoDescription.tpaName || '').trim() === ''));

  return (
    <>
      <div className={`${classes.overlay} ${loading ? classes.overlayVisible : ''}`}>
        <LoaderDialog
          open={loading}
          message="We're working on your request. Please hang tight, and we'll notify you once your case is submitted."
        />
        {isCaseCreated && (
          <SuccessDialog
            icon="images/success.svg"
            open={isCaseCreated}
            title="Submitted"
            message="Thanks for submitting the case form. We will review your case and get back to you soon. New cases will appear within 1 business day."
            buttonText="Continue"
            handleSuccess={handleSuccess}
          />
        )}
      </div>
      <AppLayout>
        <div className={classes.appContainer}>
          <ArrowBackIcon className={classes.arrowIcon} onClick={handleBack} />
          <Typography>
            ** Please upload a clear, readable copy of the entire page(s) of the statement or bill
            you received.
            <br /> This can be a photo of the bill or a scan. <br />
            ** If you are experiencing difficulties uploading the required document(s), please email
            the document(s) to <br />
            {hasClientSubmitRole && (
              <Link onClick={handleCSSEmailClick} to="/upload-file">
                <span className={classes.link}>CSS@6degreeshealth.com.</span>
              </Link>
            )}
            {!hasClientSubmitRole && (
              <Link onClick={handlePSSEmailClick} to="/upload-file">
                {(newCaseInfo.groupid === '10000SDH' || newCaseInfo.groupid === '0180988601') && (
                  <span className={classes.link}>employeehealthplan@6degreeshealth.com.</span>
                )}
                {newCaseInfo.groupid !== '10000SDH' && newCaseInfo.groupid !== '0180988601' && (
                  <span className={classes.link}>PSS@6degreeshealth.com.</span>
                )}
              </Link>
            )}
          </Typography>
          {hasClientSubmitRole && (
            <>
              <Typography className={classes.heading}>Health Plan Information</Typography>
              <div className={classes.textBoxWrapper}>
                <TextInput
                  label="Plan/Employer Name"
                  value={newCaseInfo.planname || providerInfoDescription.planname}
                  inputProps={{
                    readOnly: newCaseInfo && newCaseInfo.planname !== '',
                  }}
                  onChangeEvent={handlePlanInfoChange('planname')}
                />
                <TextInput
                  label="Group #*"
                  value={newCaseInfo.groupid || providerInfoDescription.groupid}
                  inputProps={{
                    readOnly: newCaseInfo && newCaseInfo.groupid !== '',
                  }}
                  onChangeEvent={handlePlanInfoChange('groupid')}
                />
                <TextInput
                  label="TPA Name*"
                  value={newCaseInfo.tpaName || providerInfoDescription.tpaName}
                  inputProps={{
                    readOnly: newCaseInfo && newCaseInfo.tpaName !== '',
                  }}
                  onChangeEvent={handlePlanInfoChange('tpaName')}
                />
              </div>
            </>
          )}
          <div className={classes.formWrapper}>
            <DragDrop
              upLoadedFile={uploadedFiles}
              onFilesAccepted={handleFilesAccepted}
              onRemoveFile={handleRemoveFile}
            />
            <div className={classes.textBox}>
              <TextInput
                placeholder=""
                id="outlined-multiline-static"
                label="Additional Comments (If any)"
                value={comments}
                onChange={val => handleChange(val, 'additionalComment')}
                multiline
                minRow={11}
                maxRow={11}
              />
            </div>
          </div>
          <div className={classes.buttonStyle}>
            <Button type="button" className={classes.backBtn} onClick={handleBack}>
              BACK
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.successBtn}
              onClick={handleSubmit}
              disabled={disableSubmitBtn}
            >
              {loading ? 'Submitting...' : 'SUBMIT'}
            </Button>
          </div>
        </div>
      </AppLayout>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadFiles);
